import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { StyledSectionContainer } from '../../../../globalStyled';
import {
  fontCondensed,
  fontDisplay,
  fontUi,
  viewMoreLink,
} from '../../../../theme/typography';

export const StyledPage = styled.div`
  width: 100%;
  padding: 0 0 40px;
`;

export const StyledTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  margin: 0 0 8px;
  @media (min-width: 48em) {
    width: auto;
    margin: 0 25px 8px;
  }

  > h1 {
    ${fontDisplay};
    font-size: 28px;
    line-height: 1.15;
    letter-spacing: -0.02em;
    text-transform: none;
    margin: 0;
    color: ${({ theme }) => theme.text};
  }
`;

export const StyledNet = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

export const StyledNetLabel = styled.span`
  ${fontUi};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledNetValue = styled.strong`
  ${fontDisplay};
  font-size: 22px;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: ${({ theme, $tone }) => {
    if ($tone === 'plus') return theme.success;
    if ($tone === 'minus') return theme.danger;
    return theme.text;
  }};
`;

export const StyledIntro = styled.p`
  ${fontUi};
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textMuted};
  @media (min-width: 48em) {
    margin: 0 25px 16px;
  }
`;

export const StyledBackLink = styled(Link)`
  ${viewMoreLink};
  margin: 0 0 10px;
  font-size: 13px;
  @media (min-width: 48em) {
    margin: 0 25px 10px;
  }
`;

export const StyledColumns = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 25px;
  width: 100%;

  @media only screen and (max-width: 1199px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const StyledSection = styled(StyledSectionContainer)`
  justify-content: flex-start;
  overflow: hidden;
  min-width: 0;
  flex: 1;
  max-width: 640px;
  margin-bottom: 0;

  @media only screen and (max-width: 1199px) {
    width: 100%;
    max-width: none;
  }
`;

export const StyledHead = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 52px;
  min-height: 52px;
  padding: 0 16px 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
`;

export const StyledTitle = styled.h3`
  ${fontDisplay};
  margin: 0;
  font-size: 18px;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.text};
`;

export const StyledCount = styled.span`
  ${fontUi};
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => theme.textMuted};
  white-space: nowrap;
`;

export const StyledChartWrap = styled.div`
  position: relative;
  height: 220px;
  padding: 8px 12px 0;
  flex-shrink: 0;
`;

export const StyledChartCenter = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

export const StyledCenterValue = styled.strong`
  ${fontDisplay};
  font-size: 22px;
  letter-spacing: -0.03em;
  color: ${({ theme }) => theme.text};
`;

export const StyledCenterLabel = styled.span`
  ${fontUi};
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledTooltip = styled.div`
  padding: 8px 10px;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
  max-width: 220px;
`;

export const StyledTooltipName = styled.p`
  ${fontCondensed};
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
`;

export const StyledTooltipMeta = styled.p`
  ${fontUi};
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const StyledTh = styled.th`
  ${fontUi};
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textFaint};
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
  white-space: nowrap;
`;

export const StyledTr = styled.tr`
  cursor: pointer;
  background: ${({ theme, $active }) =>
    $active ? theme.hover : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.hover};
  }

  td {
    border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
  }

  &:last-child td {
    border-bottom: 0;
  }
`;

export const StyledTd = styled.td`
  ${fontUi};
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  vertical-align: middle;
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
`;

export const StyledPlayer = styled.span`
  ${fontCondensed};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

export const StyledSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color }) => $color || 'currentColor'};
`;

export const StyledPlayerName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledLoan = styled.span`
  ${fontUi};
  margin-left: 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledClub = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 180px;
`;

export const StyledClubName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledCrest = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex-shrink: 0;
`;

export const StyledCrestFallback = styled.span`
  ${fontDisplay};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.inset};
  color: ${({ theme }) => theme.textMuted};
  font-size: 10px;
`;

export const StyledFee = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  white-space: nowrap;
`;

export const StyledEmpty = styled.p`
  ${fontUi};
  margin: 0;
  padding: 16px 20px 20px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledSeasonTabs = styled.div`
  display: flex;
  gap: 24px;
  margin: 16px 0 24px;
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
  button {
    ${fontUi};
    padding: 12px 4px;
    border: 0;
    border-bottom: 3px solid transparent;
    background: transparent;
    color: ${({ theme }) => theme.textFaint};
    cursor: pointer;
    font-weight: 600;
    &[aria-selected='true'] {
      color: ${({ theme }) => theme.text};
      border-bottom-color: var(--tol-header-accent);
    }
    &:focus-visible {
      outline: 2px solid var(--tol-header-accent);
    }
  }
  @media (min-width: 48em) {
    margin: 16px 25px 24px;
  }
`;

export const StyledSeasonPicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
  @media (min-width: 48em) {
    margin: 0 25px 12px;
  }
`;

export const StyledSeasonChip = styled.button`
  ${fontUi};
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.header : theme.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.header : theme.surface};
  color: ${({ theme, $active }) =>
    $active ? theme.headerAccent || theme.buttonText : theme.textMuted};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${({ theme, $active }) =>
      $active ? theme.header : theme.hover};
  }
  &:focus-visible {
    outline: 2px solid var(--tol-header-accent);
  }
`;

export const StyledUniverseSection = styled(StyledSection)`
  max-width: none;
  width: 100%;
`;
export const StyledTextButton = styled.button`
  font: inherit;
  color: inherit;
  background: none;
  padding: 0;
  border: 0;
  text-align: inherit;
  cursor: pointer;
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid var(--tol-header-accent);
  }
`;
